import { LocalCombinationItemsListView } from "@coworker/apprestructured/src/media-maintenance/components/LocalCombinations/LocalCombinationItemsListView/LocalCombinationItemsListView";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import { useItemsInfoBatch } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { ItemsStock } from "@coworker/apprestructured/src/shared/hooks/item/useItemsStock";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import React, { useMemo } from "react";
import { isCombinationParentArticle } from "../Helpers/articleHelpers";
import { AggregatedArticleListItem } from "../Molecules/AggregatedArticleListItem";
import { selectArticleIdNbrArticles } from "../Molecules/MediaArticles.helper";
import { MediasViewSearchListItemSkeleton } from "../Molecules/MediasViewSearchListItemSkeleton";
import { AggregatedArticle } from "../hooks/useAggregatedArticles";
import { ArticleListItemMobile, ListWrapper } from "../styles/styles";
import {
  ArticleIdNbrArticles,
  Articles,
  EdsData,
  SalesStopData,
} from "../types/article";
import { Media } from "../types/media";
import { TaskLinks } from "../types/taskLink";

interface AggregatedArticleListProps {
  articles: AggregatedArticle[] | undefined;
  combinationArticles: Record<string, Articles>;
  media: Media;
  isSelectArticlesMode: boolean;
  taskLinks: TaskLinks;
  v2TasksMap: Map<string, boolean>;
  edsData: EdsData[];
  itemsStockInfo: ItemsStock | undefined;
  salesStopData: SalesStopData[];
  selectedArticleIdsNbrArticles: ArticleIdNbrArticles[];
  setSelectedArticleIdsNbrArticles: React.Dispatch<
    React.SetStateAction<ArticleIdNbrArticles[]>
  >;
}

export const AggregatedArticleList = React.forwardRef<
  HTMLDivElement,
  AggregatedArticleListProps
>(
  (
    {
      articles,
      combinationArticles,
      media,
      isSelectArticlesMode,
      taskLinks,
      v2TasksMap,
      edsData,
      salesStopData,
      itemsStockInfo,
      selectedArticleIdsNbrArticles,
      setSelectedArticleIdsNbrArticles,
    },
    ref
  ) => {
    const itemNos = React.useMemo(
      () =>
        articles
          ?.filter(
            (article) => !isCombinationParentArticle(article.productArticleType)
          )
          .map((a) => a.productArticleId)
          .filter(Boolean),
      [articles]
    );
    const { data: itemsInfo, isLoading } = useItemsInfoBatch(itemNos ?? []);
    const itemInfoMap = useMemo(() => {
      const map = new Map<string, Item>();

      itemsInfo?.forEach((item) => {
        map.set(item.no, item);
      });

      return map;
    }, [itemsInfo]);
    if (!articles) {
      return (
        <FixaListView id="articles-list">
          <MediasViewSearchListItemSkeleton key="medias-view-article-0" />
          <MediasViewSearchListItemSkeleton key="medias-view-article-1" />
          <MediasViewSearchListItemSkeleton key="medias-view-article-2" />
          <MediasViewSearchListItemSkeleton key="medias-view-article-3" />
        </FixaListView>
      );
    } else {
      return (
        <ListWrapper ref={ref}>
          {articles.map((uniqueArticle) => (
            <React.Fragment key={`fragment-${uniqueArticle.id}`}>
              <ArticleListItemMobile key={uniqueArticle.id}>
                <AggregatedArticleListItem
                  key={`aggregated-article-${uniqueArticle.id}`}
                  article={uniqueArticle}
                  media={media}
                  itemInfo={
                    itemInfoMap.get(uniqueArticle.productArticleId) ?? null
                  }
                  itemInfoLoading={isLoading}
                  taskLinks={taskLinks}
                  v2TasksMap={v2TasksMap}
                  stockInfo={
                    itemsStockInfo
                      ? itemsStockInfo[uniqueArticle.productArticleId]
                      : undefined
                  }
                  edsData={
                    edsData?.find(
                      (eds: { productArticleId: string }) =>
                        eds.productArticleId === uniqueArticle.productArticleId
                    ) ??
                    uniqueArticle.eds ??
                    null
                  }
                  salesStopData={
                    salesStopData?.find(
                      (salesStop) =>
                        salesStop.productArticleId ===
                        uniqueArticle.productArticleId
                    ) ?? null
                  }
                  badges4AK1={uniqueArticle.badges4AK1 ?? []}
                  itemTags={uniqueArticle.itemTags ?? []}
                  selectArticleIdsMode={isSelectArticlesMode}
                  handleSelectedItems={(articleIdNbrArticles) =>
                    selectArticleIdNbrArticles(
                      articleIdNbrArticles,
                      selectedArticleIdsNbrArticles,
                      setSelectedArticleIdsNbrArticles
                    )
                  }
                  isSelected={
                    !!selectedArticleIdsNbrArticles.find(
                      (item) => item.articleId === uniqueArticle.id
                    )
                  }
                />
              </ArticleListItemMobile>
              {isCombinationParentArticle(uniqueArticle.productArticleType) && (
                <LocalCombinationItemsListView
                  parentArticle={uniqueArticle}
                  combinationArticles={combinationArticles}
                  itemsStockInfo={itemsStockInfo}
                  edsData={edsData}
                  salesStopData={salesStopData}
                  mediaItemViewType={MediaItemViewType.AGGREGATED_ITEM_VIEW}
                />
              )}
            </React.Fragment>
          ))}
        </ListWrapper>
      );
    }
  }
);
